import {
  useMarkThreadAsResolved,
  useMarkThreadAsUnresolved,
} from "@liveblocks/react";
import { Thread } from "@liveblocks/react-ui";
import { useStore } from "@xyflow/react";
import { CheckCircle2, CircleDot, MessageCircle, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CanvasPinAffordance } from "../components/CanvasPinAffordance";
import { nodeBoundsById } from "../utils/nodeBounds";
import { useCanvasComments } from "./CanvasCommentsContext";
import { resolvePinPosition } from "./threadPlacement";
import {
  dismissesThreadWindow,
  dragThreadWindow,
  THREAD_SCROLLER_SELECTOR,
  threadWindowOrigin,
  threadWindowPointerTarget,
  threadWindowSessionFor,
  type ThreadWindowSession,
} from "./threadWindow";
import { useCanvasCommentThreads } from "./useCanvasCommentThreads";

/** Screen-px width of the comment surfaces the composer and the thread share. */
export const COMMENT_WINDOW_WIDTH = 340;

/** A thread opens on its latest comment, the way every chat surface does. */
function scrollToLatest(el: HTMLElement | null) {
  const list = el?.querySelector(THREAD_SCROLLER_SELECTOR);
  if (list) list.scrollTop = list.scrollHeight;
}

/**
 * The expanded thread.
 *
 * `Thread`, not `FloatingThread`: the latter is a popover pinned to its
 * trigger and cannot be dragged away from the pin, which the design requires.
 *
 * The header drag moves the WINDOW ONLY, into a session that is discarded on
 * close — the opposite of `ChatSessionNode`'s header drag, which relocates the
 * node itself. A comment's position belongs to the thread, not to where
 * someone last pushed its window. That same drag is what detaches the window
 * from the pin and pins it open; see `threadWindow.ts` for the two states,
 * what moves each and what dismisses each.
 */
export function CanvasCommentThreadWindow() {
  const { openThreadId, closeThread, reportThreadWindowSession } =
    useCanvasComments();
  const { threads } = useCanvasCommentThreads();
  const nodes = useStore((s) => s.nodes);
  const markResolved = useMarkThreadAsResolved();
  const markUnresolved = useMarkThreadAsUnresolved();

  const [stored, setStored] = useState<ThreadWindowSession | null>(null);
  const windowRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origin: { x: number; y: number };
  } | null>(null);

  // Two guards, because one of them is a frame late: the effect discards the
  // session on close, and the derivation refuses one belonging to another
  // thread in the commit before that lands.
  const session = useMemo(
    () => (openThreadId ? threadWindowSessionFor(stored, openThreadId) : null),
    [stored, openThreadId],
  );

  useEffect(() => {
    setStored(null);
  }, [openThreadId]);

  // The pin click that would close this window lands on the pin, not here, and
  // needs to know whether the window has been moved.
  useEffect(() => {
    reportThreadWindowSession(session);
    return () => reportThreadWindowSession(null);
  }, [session, reportThreadWindowSession]);

  // Capture phase on the document: the pins stop propagation on their own
  // pointerdowns, and this listener has to see them anyway to tell them apart.
  // Mounted only while a window is open, so the pin click that opened it is
  // already past.
  useEffect(() => {
    if (!session) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (dismissesThreadWindow(session, threadWindowPointerTarget(target))) {
        closeThread();
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [session, closeThread]);

  const thread = threads.find((t) => t.id === openThreadId);

  const position = useMemo(() => {
    if (!thread) return null;
    return resolvePinPosition(thread.metadata, nodeBoundsById(nodes));
  }, [thread, nodes]);

  // React 19 ref cleanup, not a null call: the exiting window's detach would
  // otherwise land after this one's attach and blank the ref.
  const attachWindow = useCallback((el: HTMLDivElement) => {
    windowRef.current = el;
    scrollToLatest(el);
    return () => {
      if (windowRef.current === el) windowRef.current = null;
    };
  }, []);

  const commentCount = thread?.comments.length ?? 0;
  useEffect(() => {
    scrollToLatest(windowRef.current);
  }, [commentCount]);

  const onHeaderPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // The header's own verbs are not a grab. Read off the target rather than
      // stopping the event on the button, which would cost it the vendor's
      // document-level popover dismissal.
      if (e.target instanceof Element && e.target.closest("button")) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origin: session?.offset ?? { x: 0, y: 0 },
      };
    },
    [session],
  );

  // The pin cannot move while its own window is being dragged, so the position
  // this closure holds is the one to freeze the window against.
  const onHeaderPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || !openThreadId || !position) return;
      setStored((current) =>
        dragThreadWindow(
          threadWindowSessionFor(current, openThreadId),
          drag.origin,
          { x: e.clientX - drag.startX, y: e.clientY - drag.startY },
          position,
        ),
      );
    },
    [openThreadId, position],
  );

  const endHeaderDrag = useCallback(() => {
    dragRef.current = null;
  }, []);

  const toggleResolved = useCallback(() => {
    if (!thread) return;
    if (thread.resolved) {
      markUnresolved(thread.id);
      return;
    }
    markResolved(thread.id);
    closeThread();
  }, [thread, markResolved, markUnresolved, closeThread]);

  if (!thread || !position || !session) return null;

  const origin = threadWindowOrigin(session, position);

  return (
    <CanvasPinAffordance
      x={origin.x}
      y={origin.y}
      open
      centered
      panelKey={thread.id}
      panelWidth={COMMENT_WINDOW_WIDTH}
      panelOffset={session.offset}
      panel={
        // `nopan`, and no pointerdown of our own: the vendor closes its
        // popovers from a document listener, which sits above React's root
        // container, so a synthetic stopPropagation here reaches the native
        // event and the emoji picker never hears the click that should shut
        // it. Panning is what the class handles, and handles properly — the
        // drag d3-zoom takes is a mousedown, which that stopPropagation never
        // touched.
        <div
          ref={attachWindow}
          data-canvas-comment-window={thread.id}
          className="canvas-comment-window canvas-comments nopan nowheel overflow-hidden rounded-lg border border-n-10 bg-popover shadow-elev-4"
        >
          {/* Identity layer, thread verbs and drag handle in one: MessageCircle
              is the comment glyph, distinct from chat's MessagesSquare. h-10 is
              --bar-h-compact, the rung that carries a --size-7 control with
              --space-1_5 either side. */}
          <div
            className="flex h-10 shrink-0 cursor-grab select-none items-center gap-1.5 border-b border-n-10 px-3 text-sm text-n-50 active:cursor-grabbing"
            onPointerDown={onHeaderPointerDown}
            onPointerMove={onHeaderPointerMove}
            onPointerUp={endHeaderDrag}
            onPointerCancel={endHeaderDrag}
          >
            <MessageCircle className="size-3.5" />
            <span className="flex-1">Comment</span>
            {/* aria-label, no title: the family's tooltips are hidden
                (`.lb-tooltip { display: none }`) and a native one would be the
                only tooltip in the window. */}
            <ThreadWindowAction
              label={thread.resolved ? "Unresolve thread" : "Resolve thread"}
              onClick={toggleResolved}
            >
              {thread.resolved ? (
                <CircleDot className="size-3.5" />
              ) : (
                <CheckCircle2 className="size-3.5" />
              )}
            </ThreadWindowAction>
            <ThreadWindowAction label="Close thread" onClick={closeThread}>
              <X className="size-3.5" />
            </ThreadWindowAction>
          </div>
          <Thread
            thread={thread}
            // Resolving is a thread verb and lives in the header; on the first
            // comment it read as the comment's own.
            showResolveAction={false}
            showReactions
            onResolvedChange={(resolved) => {
              if (resolved) closeThread();
            }}
            onThreadDelete={closeThread}
          />
        </div>
      }
    />
  );
}

/** A header verb; `onHeaderPointerDown` keeps its press out of the drag. */
function ThreadWindowAction({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-sm text-n-50 outline-none transition-colors hover:bg-n-10 hover:text-n-90 focus-visible:outline-2 focus-visible:outline-rust"
    >
      {children}
    </button>
  );
}
